import React from 'react';
import { AlertCircle } from 'lucide-react';

/**
 * Reusable confirmation modal.
 *
 * Usage:
 *   <ConfirmModal
 *     isOpen={!!confirmModal}
 *     title="Suspend Business"
 *     description="This will block all access immediately."
 *     confirmLabel="Suspend"
 *     confirmClass="bg-red-600 hover:bg-red-700"
 *     onConfirm={handleConfirm}
 *     onClose={() => setConfirmModal(null)}
 *   />
 *
 * Children slot (optional) -- use when you need a form field in the body,
 * e.g. capturing a rejection reason on a destructive action. Rendered
 * between the description and the button row. All existing callers that
 * don't pass children see the exact same layout as before.
 *
 *   <ConfirmModal ... onConfirm={() => onConfirm(reason)}>
 *     <textarea value={reason} onChange={...} />
 *   </ConfirmModal>
 *
 * confirmDisabled -- optional, disables the Confirm button (useful when
 * required body fields are empty). Defaults to false.
 */
const ConfirmModal = ({
  isOpen,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmClass = 'bg-rose-600 hover:bg-rose-700',
  icon: Icon = AlertCircle,
  iconClass = 'text-red-600',
  iconBgClass = 'bg-red-100',
  confirmDisabled = false,
  onConfirm,
  onClose,
  children,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        <div className="flex items-start gap-4 mb-5">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${iconBgClass}`}>
            <Icon className={`w-5 h-5 ${iconClass}`} />
          </div>
          <div>
            <h3 className="text-[15px] font-semibold text-slate-800">{title}</h3>
            {description && (
              <p className="text-[13px] text-slate-500 mt-1">{description}</p>
            )}
          </div>
        </div>
        {children && (
          <div className="mb-5">
            {children}
          </div>
        )}
        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-[13px] font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            disabled={confirmDisabled}
            className={`px-4 py-2 text-[13px] font-medium text-white rounded-lg transition-colors ${confirmClass} disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
